import { Plus } from "lucide-react";
import type { AgentStatus, SessionInfo } from "@/api/types";
import { deriveSessionState } from "@/api/types";

interface SessionTabBarProps {
  sessions: SessionInfo[] | undefined;
  /** Session IDs currently open in the grid. Non-null means tab is "active". */
  openSessionIds: Set<string>;
  /** Called when a session tab is clicked. Impl decides: open, focus, or close */
  onToggleSession: (sessionId: string) => void;
  onAddNewSession: () => void;
  /** How many total slots are open in the grid (including new-slot placeholders) */
  slotsOpen: number;
  maxSlots: number;
}

const MAX_LABEL_LEN = 28;

export function SessionTabBar({
  sessions,
  openSessionIds,
  onToggleSession,
  onAddNewSession,
  slotsOpen,
  maxSlots,
}: SessionTabBarProps) {
  const atCapacity = slotsOpen >= maxSlots;

  // Sort: healthy sessions first (by recent activity), stalled/failed last.
  // Keeps the bar scanning cleanly when the user has accumulated zombies
  // over a long desk session.
  const sorted = (sessions ?? []).slice().sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    const at = a.lastActivityAt ?? a.createdAt;
    const bt = b.lastActivityAt ?? b.createdAt;
    return bt.localeCompare(at);
  });

  return (
    <div
      className="flex items-center gap-1 px-3 py-1.5 border-b overflow-x-auto hide-scrollbar"
      style={{
        borderColor: "var(--blt)",
        background: "var(--surface)",
        flexShrink: 0,
      }}
    >
      {sorted.map((s) => {
        const isOpen = openSessionIds.has(s.sessionId);
        const canClickOpen = isOpen || !atCapacity;
        const stalled = isStalled(s);
        return (
          <button
            key={s.sessionId}
            type="button"
            onClick={() => canClickOpen && onToggleSession(s.sessionId)}
            disabled={!canClickOpen}
            className="shrink-0 px-2 py-1 rounded text-[11px] flex items-center gap-1.5 transition-all"
            style={{
              fontFamily: "var(--font-mono)",
              background: isOpen
                ? "var(--white)"
                : stalled
                  ? "color-mix(in srgb, var(--warm) 6%, transparent)"
                  : "color-mix(in srgb, var(--t4) 4%, transparent)",
              color: isOpen
                ? "var(--t1)"
                : stalled
                  ? "var(--warm)"
                  : "var(--t3)",
              border: isOpen
                ? "1px solid var(--border)"
                : stalled
                  ? "1px dashed color-mix(in srgb, var(--warm) 40%, transparent)"
                  : "1px solid transparent",
              fontWeight: isOpen ? 500 : 400,
              boxShadow: isOpen
                ? "0 1px 2px rgba(26, 18, 16, 0.04)"
                : undefined,
              opacity: canClickOpen ? (stalled && !isOpen ? 0.7 : 1) : 0.4,
              cursor: canClickOpen ? "pointer" : "not-allowed",
            }}
            title={
              stalled
                ? `${stallReason(s)} · click to open and reset`
                : isOpen
                  ? `Close tile · ${s.invocation} · ${s.model}`
                  : atCapacity
                    ? `Grid is full (${maxSlots} max). Close a tile first.`
                    : `Open in grid · ${s.invocation} · ${s.model}`
            }
          >
            <SessionTabDot status={s.agentStatus} />
            <span>{tabLabel(s)}</span>
            {isOpen && (
              <span
                className="text-[9px] ml-0.5"
                style={{ color: "var(--t4)", letterSpacing: "0.04em" }}
              >
                ×
              </span>
            )}
          </button>
        );
      })}

      <button
        type="button"
        onClick={onAddNewSession}
        disabled={atCapacity}
        className="shrink-0 px-2 py-1 rounded text-[11px] font-medium flex items-center gap-1"
        style={{
          fontFamily: "var(--font-mono)",
          color: atCapacity ? "var(--t4)" : "var(--accent)",
          background: atCapacity
            ? "transparent"
            : "color-mix(in srgb, var(--accent) 8%, transparent)",
          border: "1px solid transparent",
          opacity: atCapacity ? 0.5 : 1,
          cursor: atCapacity ? "not-allowed" : "pointer",
        }}
        title={atCapacity ? `Grid full (${maxSlots})` : "Start a new session"}
      >
        <Plus size={11} />
        new
      </button>

      <div className="flex-1" />

      {slotsOpen > 0 && (
        <span
          className="shrink-0 text-[10px] tabular-nums"
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--t4)",
            letterSpacing: "0.04em",
          }}
          title="Open tiles / max"
        >
          {slotsOpen}/{maxSlots} open
        </span>
      )}
    </div>
  );
}

function SessionTabDot({ status }: { status: AgentStatus }) {
  const color =
    status === "running" || status === "starting"
      ? "var(--info)"
      : status === "completed"
        ? "var(--ok)"
        : status === "failed"
          ? "var(--err)"
          : status === "stale"
            ? "var(--warm)"
            : "var(--t4)";
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full align-middle shrink-0"
      style={{
        background: color,
        animation:
          status === "running" || status === "starting"
            ? "pulse 1.6s ease-in-out infinite"
            : undefined,
      }}
    />
  );
}

function tabLabel(s: SessionInfo): string {
  const raw = s.lastMessage ?? "";
  const clean = stripMarkdown(raw).slice(0, MAX_LABEL_LEN).trim();
  if (clean.length > 2) return clean;
  const shortModel =
    s.model
      .split("/")
      .pop()
      ?.replace(/-\d+(\.\d+)?-?.*$/, "")
      ?.slice(0, 16) ?? "";
  return shortModel ? `${s.invocation} · ${shortModel}` : s.invocation;
}

/**
 * Is this session stalled / unusable for a follow-up message? True for
 * dead-manager sessions (agentStatus=running but managerAlive=false),
 * classic stale agents, or sessions that failed on a sprite that's gone.
 */
function isStalled(s: SessionInfo): boolean {
  const effective = deriveSessionState(s);
  return effective === "stalled";
}

function stallReason(s: SessionInfo): string {
  if (!s.managerAlive && s.agentStatus === "running") {
    return "Agent heartbeat lost";
  }
  if (s.agentStatus === "stale") return "Agent went stale";
  return "Session stalled";
}

/**
 * Sort rank: lower = earlier in the tab bar. Healthy sessions come first,
 * stalled/failed last so the eye lands on what the user can act on.
 */
function rank(s: SessionInfo): number {
  const state = deriveSessionState(s);
  if (state === "running" || state === "thinking" || state === "starting")
    return 0;
  if (state === "completed") return 1;
  if (state === "interrupted") return 2;
  if (state === "failed") return 3;
  if (state === "stalled") return 4;
  return 5;
}

function stripMarkdown(s: string): string {
  let out = s;
  out = out.replace(/```[\s\S]*?```/g, " ");
  out = out.replace(/```[\s\S]*/g, " ");
  out = out.replace(/`([^`]+)`/g, "$1");
  out = out.replace(/\*\*([^*]+)\*\*/g, "$1");
  out = out.replace(/\*([^*]+)\*/g, "$1");
  out = out.replace(/^#+\s+/gm, "");
  out = out.replace(/!?\[([^\]]+)\]\([^)]+\)/g, "$1");
  out = out.replace(/[_|\-=]{3,}/g, " ");
  out = out.replace(/\s+/g, " ").trim();
  return out;
}
